import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { GameModel, GameMoveModel } from '../types/GamesModel';
import { Color } from '../types/Color';
import WalletMirror from './WalletMirror';
import GamesListItemChessboard from './GamesListItemChessboard';
import GameContext from '../context/GameContext';
import AppContext from '../context/AppContext';
import AnnotationsContext from '../context/AnnotationsContext';
import { useMemo, useState } from 'react';
import { Chessviewer } from '../utils/Chessviewer';
import { Button, Modal } from 'react-bootstrap';
import GamesListItemTitle from './GamesListItemTitle';
import GamesListItemEditor from './GamesListItemEditor';
import GamesListItemEditorOptions from './GamesListItemEditorOptions';
import GamesOverlayEvaluate from './GamesOverlayEvaluate';
import GamesOverlayRepair from './GamesOverlayRepair';
import EvaluationOverlayProvider from './EvaluationOverlayProvider';
import CorrectionsProvider, { useCorrections } from './CorrectionsProvider';
import PracticeProvider, { usePractice } from './PracticeProvider';
import CommentPanel from './CommentPanel';
import CommentTreeView from './CommentTreeView';
import AnnotationsProvider from './AnnotationsProvider';
import UserTagsProvider from './UserTagsProvider';
import TagPicker from './TagPicker';
import GamesAPI from '../api/GamesAPI';
import { GridLoader } from 'react-spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faComment,
  faChessKing,
} from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../store';
import { setGameIdForModal, clearOpenCorrect } from '../reducers/uiReducer';
import { setParams } from '../reducers/paramsReducer';

export default function GamesListItemModal(props: {
  gameId: number;
  onHide: () => void;
}): React.ReactNode {
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState<GameModel>();
  // The fetch 404s both for a missing game and one the viewer can't see, so the modal shows a message
  // instead of spinning.
  const [error, setError] = useState(false);

  const { gameId, onHide } = props;
  const { isMobile } = useContext(AppContext);
  const dispatch = useAppDispatch();
  const gameRefreshKey = useAppSelector((state) => state.uiReducer.gameRefreshKey);
  // The Player filter's player, highlighted in the matchup.
  const selectedUser = useAppSelector((state) => state.paramsReducer.params.username);
  // One player is always in focus: the filtered player when in this game, otherwise White (matching the
  // board's default orientation and the corrections POV).
  const focusName = ((): string | undefined => {
    const u = selectedUser?.toLowerCase();
    if (u) {
      if (game?.White?.toLowerCase() === u) return game.White;
      if (game?.Black?.toLowerCase() === u) return game.Black;
    }
    return game?.White;
  })();
  const isSelected = (name: string | undefined) =>
    name != null && focusName != null && name.toLowerCase() === focusName.toLowerCase();

  // Clicking a name sets the Player filter to them, which reorients the board to their side.
  const viewAsPlayer = (name: string | undefined) => {
    if (name) {
      dispatch(setParams({ username: name, page: 0 }));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await GamesAPI.getById(gameId);
      setGame(data ?? undefined);
      setError(data == null);
    } catch {
      setGame(undefined);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [gameId, gameRefreshKey]);

  useEffect(() => {
    load();
  }, [load]);

  const _onHide = () => {
    onHide();
  };

  return (
    <Modal
      size="xl"
      show={true}
      onHide={_onHide}
      dialogClassName="cm-game-modal"
      scrollable={!isMobile}
      fullscreen={isMobile ? true : undefined}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex flex-fill align-items-center">
          {loading ? (
            <h3 className="mb-0">Loading...</h3>
          ) : error || game == null ? (
            <h3 className="mb-0">Game unavailable</h3>
          ) : (
            <div className="cm-game-header">
              <span className="cm-game-matchup">
                <span className="cm-side">
                  <button
                    type="button"
                    className={`cm-name${isSelected(game.White) ? ' cm-name-active' : ''}`}
                    onClick={() => viewAsPlayer(game.White)}
                    title={`View the game as ${game.White}`}
                  >
                    {game.White}
                  </button>
                  <span className="cm-elo cm-mono">{game.WhiteElo}</span>
                  {game.Result == '1-0' ? (
                    <span className="cm-crown">
                      <FontAwesomeIcon icon={faChessKing} />
                    </span>
                  ) : null}
                </span>
                <span className="cm-vs">vs</span>
                <span className="cm-side">
                  <button
                    type="button"
                    className={`cm-name${isSelected(game.Black) ? ' cm-name-active' : ''}`}
                    onClick={() => viewAsPlayer(game.Black)}
                    title={`View the game as ${game.Black}`}
                  >
                    {game.Black}
                  </button>
                  <span className="cm-elo cm-mono">{game.BlackElo}</span>
                  {game.Result == '0-1' ? (
                    <span className="cm-crown">
                      <FontAwesomeIcon icon={faChessKing} />
                    </span>
                  ) : null}
                </span>
              </span>
              {game.Termination ? <span className="cm-termination">{game.Termination}</span> : null}
            </div>
          )}
          {/* This modal covers the header counter and repairs happen in here. */}
          <WalletMirror />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="cm-modal-body">
        {loading ? (
          <div className="cm-modal-loading">
            <GridLoader color="var(--sc-accent)"></GridLoader>
          </div>
        ) : game != null ? (
          <GamesListItemInternal game={game} />
        ) : (
          <div className="cm-modal-loading">
            <div className="alert alert-warning mb-0 text-center" role="alert">
              <h4>This game isn’t available</h4>
              <p className="mb-0">
                It may have been removed, or it’s in a private space you don’t have access to. If it’s
                yours, sign in and try again.
              </p>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
function GamesListItemInternal(props: { game: GameModel }): React.ReactNode {
  const { game } = props;
  const [currentMove, setCurrentMove] = useState<GameMoveModel>();
  const chessviewerRef = useRef<Chessviewer>();
  const { color, username } = useAppSelector((state) => state.paramsReducer.params);

  // Default side: an explicit colour filter, else the selected user's side, else White. The flip button
  // toggles it; the board, mini board and eval bar follow.
  const initialOrientation = useMemo<'white' | 'black'>(() => {
    if (color == Color.White) return 'white';
    if (color == Color.Black) return 'black';
    if (username != null && username != '') {
      const u = username.toLowerCase();
      if (game.Black?.toLowerCase() === u) return 'black';
      if (game.White?.toLowerCase() === u) return 'white';
    }
    return 'white';
  }, [color, username, game.White, game.Black]);

  const [orientation, setOrientation] = useState<'white' | 'black'>(initialOrientation);
  // Re-derive when the filter/user (and thus default side) changes.
  useEffect(() => setOrientation(initialOrientation), [initialOrientation]);
  const dispatch = useAppDispatch();

  // Live engine eval (White win %) for a dragged position; null during playback.
  const [analysisWhiteWin, setAnalysisWhiteWin] = useState<number | null>(null);
  // Whether dragging / exploration mode is on (mirrors Chessviewer).
  const [dragging, setDragging] = useState(false);

  // Flip switches which player you're reviewing: it sets the Player filter to the other side, which
  // reorients the board and re-scopes the corrections. A plain visual flip only when that side has no
  // name.
  const flip = useCallback(() => {
    const other = orientation === 'white' ? game.Black : game.White;
    if (other != null && other !== '') {
      dispatch(setParams({ username: other, page: 0 }));
    } else {
      setOrientation((o) => (o === 'white' ? 'black' : 'white'));
    }
  }, [orientation, game.White, game.Black, dispatch]);

  const contextValue = useMemo(
    () => ({
      currentMove,
      setCurrentMove,
      game,
      chessviewerRef,
      orientation,
      flip,
      analysisWhiteWin,
      setAnalysisWhiteWin,
      dragging,
      setDragging,
    }),
    [currentMove, game, orientation, flip, analysisWhiteWin, dragging],
  );

  return (
    <GameContext.Provider value={contextValue}>
      <AnnotationsProvider gameId={game.Id} initialFavorites={game.FavoriteMovePgns ?? []}>
        <UserTagsProvider gameId={game.Id}>
          <EvaluationOverlayProvider>
            <CorrectionsProvider gameId={game.Id} enabled={game.LastEvaluation != null}>
              <PracticeProvider>
                <GameBodyWithPanel />
              </PracticeProvider>
            </CorrectionsProvider>
          </EvaluationOverlayProvider>
        </UserTagsProvider>
      </AnnotationsProvider>
    </GameContext.Provider>
  );
}

// One scrolling overlay: board · moves · comments in a row, then the action row, the game-level
// comments and prev/next game. On mobile the columns stack.
function GameBodyWithPanel(): React.ReactNode {
  const { isMobile } = useContext(AppContext);
  const { game, currentMove } = useContext(GameContext);
  const { comments } = useContext(AnnotationsContext);

  // Keep the address bar on the move being reviewed (…&move=12w) so the URL is shareable. replaceState
  // rather than the router, so it neither feeds back into the deep-link effect nor piles up history.
  useEffect(() => {
    const hash = window.location.hash || '#/';
    const qIndex = hash.indexOf('?');
    const base = qIndex >= 0 ? hash.slice(0, qIndex) : hash;
    const sp = new URLSearchParams(qIndex >= 0 ? hash.slice(qIndex + 1) : '');
    const reflect = currentMove != null;
    if (reflect) {
      const ref = `${currentMove!.Number}${currentMove!.Color === Color.White ? 'w' : 'b'}`;
      if (sp.get('move') === ref) {
        return;
      }
      sp.set('move', ref);
    } else {
      if (!sp.has('move')) {
        return;
      }
      sp.delete('move');
    }
    const query = sp.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${base}${query ? `?${query}` : ''}`,
    );
  }, [currentMove]);
  const dispatch = useAppDispatch();
  const { games } = useAppSelector((state) => state.gamesReducer);
  const evaluated = game.LastEvaluation != null;

  // "Correct your mistakes" runs in place on this overlay's board.
  const practice = usePractice();
  // A click on the list's correction card opens straight into practice. Wait for the corrections to
  // load (practice needs the list), then clear the flag either way so it doesn't fire on the next game.
  const openCorrectForModal = useAppSelector((state) => state.uiReducer.openCorrectForModal);
  const practiceMoveId = useAppSelector((state) => state.uiReducer.practiceMoveIdForModal);
  const { loading: correctionsLoading, activeMistakes } = useCorrections();
  useEffect(() => {
    if (!openCorrectForModal || !evaluated || correctionsLoading) {
      return;
    }
    if (practiceMoveId != null) {
      practice.select(practiceMoveId);
    } else if (activeMistakes.length > 0) {
      practice.start();
    }
    dispatch(clearOpenCorrect());
  }, [
    openCorrectForModal,
    practiceMoveId,
    evaluated,
    correctionsLoading,
    activeMistakes,
    dispatch,
    practice,
  ]);

  const gameLevelComments = useMemo(
    () => comments.filter((c) => (c.MovePgn ?? null) === null),
    [comments],
  );
  const gameLevelCount = gameLevelComments.filter((c) => c.Status !== 'Removed').length;

  // Prev/next between the games loaded in the list.
  const list = games?.List ?? [];
  const currentIndex = list.findIndex((g) => g.Id === game.Id);
  const prevGameId = currentIndex > 0 ? list[currentIndex - 1].Id : null;
  const nextGameId =
    currentIndex >= 0 && currentIndex < list.length - 1 ? list[currentIndex + 1].Id : null;
  const goToGame = (id: number | null) => {
    if (id != null) {
      dispatch(setGameIdForModal(id));
    }
  };

  return (
    <div className="cm-game-overlay">
      {/* Title/meta left, (re-)evaluate pinned top-right in both states. */}
      <div className="cm-overlay-header">
        <GamesListItemTitle />
        <div className="cm-overlay-eval-top">
          <GamesOverlayEvaluate game={game} />
          <GamesOverlayRepair />
        </div>
      </div>
      {isMobile ? (
        // Mobile: one scrolling column with the board, the inline panel and the full move list, opening on
        // move 1.
        <div className="cm-game-mobile">
          <div className="cm-mobile-board">
            <GamesListItemChessboard />
          </div>
          <CommentPanel variant="inline" />
          <div className="cm-mobile-moves">
            <GamesListItemEditor />
          </div>
        </div>
      ) : (
        <div className="cm-game-row">
          <div className="cm-board-col">
            <GamesListItemChessboard />
            </div>
          <div className="cm-moves-col">
            <GamesListItemEditor />
          </div>
          <div className="cm-comments-col">
            <CommentPanel />
          </div>
        </div>
      )}

      <div className="cm-game-actions">
        <GamesListItemEditorOptions game={game} />
      </div>

      <div className="cm-game-tags">
        <TagPicker scope="Game" label="How this game went" />
      </div>

      <div className="cm-game-thread">
        <h4 className="cm-game-thread-title">
          <FontAwesomeIcon icon={faComment} className="me-2" />
          Game comments{gameLevelCount > 0 ? ` (${gameLevelCount})` : ''}
        </h4>
        <CommentTreeView
          gameId={game.Id}
          comments={gameLevelComments}
          scopeMovePgn={null}
          emptyText="No game comments yet. Start it off."
        />
      </div>

      <div className="cm-game-nav">
        <Button
          variant="outline-secondary"
          onClick={() => goToGame(prevGameId)}
          disabled={prevGameId == null}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
          Previous
        </Button>
        <Button
          variant="outline-secondary"
          onClick={() => goToGame(nextGameId)}
          disabled={nextGameId == null}
        >
          Next
          <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
        </Button>
      </div>
    </div>
  );
}

